import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { EmptyState } from "@/components/app/lists/empty-state";
import { PageHeader } from "@/components/app/shell/page-header";
import { ListSkeleton } from "@/components/app/patterns/list-skeleton";
import { ReadFailed } from "@/components/app/lists/read-failed";
import { StatTile } from "@/components/app/lists/stat-tile";
import { StatTiles } from "@/components/app/lists/stat-tiles";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { OptionSelect, SelectOption } from "@/components/ui/option-select";
import { Switch } from "@/components/ui/switch";
import { saveFile } from "@/lib/files/download";
import { useFacility } from "@/lib/facility/facility";
import { ChevronLeft, ChevronRight } from "@/lib/icons";
import { orpc } from "@/lib/orpc";
import { optionalInt, optionalOneOf, optionalString, optionalTrue, KEEP_PLACE } from "@/lib/search";
import { useCan } from "@/lib/policy";
import { EmailStatementsDialog } from "./-components/email-statements-dialog";
import { periodRange, type Half } from "./-components/pay-period";
import { PayrollActions } from "./-components/payroll-actions";
import { MissingChargesCard } from "@/routes/_authenticated/_care/therapy-payroll/-components/missing-charges-card";
import { money, PayrollPersonCard } from "./-components/payroll-tables";
import { UnitRatesDialog } from "./-components/unit-rates-dialog";
import { payrollCsv } from "./-lib/payroll-csv";

const HALVES = ["first", "second"] as const satisfies readonly Half[];
type PayrollSearch = {
  year?: number;
  month?: number;
  half?: Half;
  clinician?: string;
  unsigned?: true;
};

export const Route = createFileRoute("/_authenticated/_care/therapy-payroll/")({
  validateSearch: (search: Record<string, unknown>): PayrollSearch => ({
    year: optionalInt(search.year, 2000, 2200),
    month: optionalInt(search.month, 0, 11),
    half: optionalOneOf(HALVES, search.half),
    clinician: optionalString(search.clinician)?.slice(0, 60) || undefined,
    unsigned: optionalTrue(search.unsigned),
  }),
  component: TherapyPayroll,
});

const MONTHS = Array.from({ length: 12 }, (_, index) =>
  new Date(2000, index, 1).toLocaleDateString("en-US", { month: "long" }),
);
const dayLabel = (value: string) =>
  new Date(`${value}T00:00:00Z`).toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
  });

/** The period somebody is most likely to be running: the one we are in. */
function currentPeriod() {
  const now = new Date();
  return {
    year: now.getUTCFullYear(),
    month: now.getUTCMonth(),
    half: (now.getUTCDate() <= 15 ? "first" : "second") as Half,
  };
}
const shift = (period: ReturnType<typeof currentPeriod>, direction: 1 | -1) => {
  if (direction === 1) {
    if (period.half === "first") return { ...period, half: "second" as Half };
    const next = new Date(Date.UTC(period.year, period.month + 1, 1));
    return { year: next.getUTCFullYear(), month: next.getUTCMonth(), half: "first" as Half };
  }
  if (period.half === "second") return { ...period, half: "first" as Half };
  const previous = new Date(Date.UTC(period.year, period.month - 1, 1));
  return { year: previous.getUTCFullYear(), month: previous.getUTCMonth(), half: "second" as Half };
};

/** Per-unit pay itemized by visit, for the period payroll is run on. */
function TherapyPayroll() {
  const { facilityId } = useFacility();
  const canManage = useCan("therapy:manage");
  const params = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const patch = (next: Partial<PayrollSearch>) =>
    void navigate({ search: (prev) => ({ ...prev, ...next }), ...KEEP_PLACE });
  const now = currentPeriod();
  const period = {
    year: params.year ?? now.year,
    month: params.month ?? now.month,
    half: params.half ?? now.half,
  };
  const setPeriod = (update: (current: typeof period) => typeof period) => {
    const next = update(period);
    patch({
      year: next.year === now.year ? undefined : next.year,
      month: next.month === now.month ? undefined : next.month,
      half: next.half === now.half ? undefined : next.half,
    });
  };
  const clinician = params.clinician ?? "all";
  const setClinician = (next: string) => patch({ clinician: next === "all" ? undefined : next });
  const includeUnsigned = Boolean(params.unsigned);
  const setIncludeUnsigned = (next: boolean) => patch({ unsigned: next ? true : undefined });
  const [ratesOpen, setRatesOpen] = useState(false);
  const [emailing, setEmailing] = useState(false);

  const { from, to } = periodRange(period.year, period.month, period.half);
  const payroll = useQuery(
    orpc.care.therapy.payroll.queryOptions({
      input: {
        facilityId,
        from,
        to,
        includeUnsigned,
        ...(clinician === "all" ? {} : { therapistId: clinician }),
      },
    }),
  );
  const everyone = useQuery(
    orpc.care.therapy.payroll.queryOptions({ input: { facilityId, from, to, includeUnsigned } }),
  );

  const report = payroll.data;
  const people = report?.items ?? [];
  const thisYear = new Date().getFullYear();

  const exportCsv = () => {
    if (!report) return;
    saveFile(
      new File([payrollCsv(report)], `therapist-payroll-${from}_${to}.csv`, {
        type: "text/csv;charset=utf-8",
      }),
    );
  };

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Therapist payroll"
        count={report ? people.length : undefined}
        meta="Per-unit pay itemized by visit. Signed and co-sign-pending visits are included; drafts only when toggled."
        actions={
          canManage ? (
            <PayrollActions
              empty={people.length === 0}
              onRates={() => setRatesOpen(true)}
              onEmail={() => setEmailing(true)}
              onExport={exportCsv}
            />
          ) : null
        }
      />

      <div className="flex flex-wrap items-end gap-3 print:hidden">
        <Button
          variant="ghost"
          size="icon"
          aria-label="Previous period"
          onClick={() => setPeriod((current) => shift(current, -1))}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="payroll-month">
            Month
          </Label>
          <OptionSelect
            id="payroll-month"
            className="w-36"
            value={String(period.month)}
            onChange={(event) =>
              setPeriod((current) => ({ ...current, month: Number(event.target.value) }))
            }
          >
            {MONTHS.map((name, index) => (
              <SelectOption key={name} value={String(index)}>
                {name}
              </SelectOption>
            ))}
          </OptionSelect>
        </div>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="payroll-year">
            Year
          </Label>
          <OptionSelect
            id="payroll-year"
            className="w-28"
            value={String(period.year)}
            onChange={(event) =>
              setPeriod((current) => ({ ...current, year: Number(event.target.value) }))
            }
          >
            {[thisYear - 2, thisYear - 1, thisYear, thisYear + 1].map((year) => (
              <SelectOption key={year} value={String(year)}>
                {year}
              </SelectOption>
            ))}
          </OptionSelect>
        </div>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="payroll-half">
            Half
          </Label>
          <OptionSelect
            id="payroll-half"
            className="w-40"
            aria-label="Half of the month"
            value={period.half}
            onChange={(event) =>
              setPeriod((current) => ({ ...current, half: event.target.value as Half }))
            }
          >
            <SelectOption value="first">1st – 15th</SelectOption>
            <SelectOption value="second">16th – end</SelectOption>
          </OptionSelect>
        </div>
        <Button
          variant="ghost"
          size="icon"
          aria-label="Next period"
          onClick={() => setPeriod((current) => shift(current, 1))}
        >
          <ChevronRight className="size-4" />
        </Button>
        <div className="space-y-1">
          <Label className="text-xs" htmlFor="payroll-therapist">
            Therapist
          </Label>
          <OptionSelect
            id="payroll-therapist"
            className="w-52"
            value={clinician}
            onChange={(event) => setClinician(event.target.value)}
          >
            <SelectOption value="all">All therapists</SelectOption>
            {(everyone.data?.items ?? []).map((person) => (
              <SelectOption key={person.userId} value={person.userId}>
                {person.name || "Unnamed"}
              </SelectOption>
            ))}
          </OptionSelect>
        </div>
        <div className="flex items-center gap-2 pb-2">
          <Switch
            id="include-drafts"
            checked={includeUnsigned}
            onCheckedChange={setIncludeUnsigned}
          />
          <Label htmlFor="include-drafts" className="font-normal">
            Include drafts
          </Label>
        </div>
      </div>

      <StatTiles className="xl:grid-cols-4">
        <StatTile
          label="Pay period"
          value={`${dayLabel(from)} – ${dayLabel(to)}`}
          hint={`${period.year}`}
        />
        <StatTile label="Visits" value={report ? report.visitCount : "-"} hint="In this period" />
        <StatTile
          label="Total"
          value={report ? money(report.total) : "-"}
          hint={`${people.length} clinician${people.length === 1 ? "" : "s"}`}
        />
        <StatTile
          label="Units with no rate"
          value={report ? report.unratedUnits : "-"}
          hint="Counted but unpaid"
        />
      </StatTiles>

      {report && report.unratedUnits > 0 ? (
        <Alert>
          <AlertTitle>Units with no rate on file</AlertTitle>
          <AlertDescription>
            {report.unratedUnits === 1 ? "1 unit was" : `${report.unratedUnits} units were`}{" "}
            furnished by somebody with no pay rate for that code. Set one in Unit rates to pay them.
          </AlertDescription>
        </Alert>
      ) : null}

      {payroll.isPending || payroll.isError || people.length === 0 ? (
        <Card className="gap-0 py-0">
          {payroll.isPending ? (
            <ListSkeleton />
          ) : payroll.isError ? (
            <ReadFailed
              className="p-4"
              message="Could not load the payroll for this period."
              error={payroll.error}
              onRetry={() => void payroll.refetch()}
              retrying={payroll.isFetching}
            />
          ) : (
            <EmptyState
              title="Nothing in this period"
              description="Visits with interventions appear here once they are written up."
            />
          )}
        </Card>
      ) : (
        people.map((person) => <PayrollPersonCard key={person.userId} person={person} />)
      )}

      {report && report.missingChargeVisits.length > 0 ? (
        <MissingChargesCard rows={report.missingChargeVisits} />
      ) : null}

      <UnitRatesDialog open={ratesOpen} onOpenChange={setRatesOpen} />
      {emailing ? (
        <EmailStatementsDialog
          people={people}
          facilityId={facilityId}
          from={from}
          to={to}
          onClose={() => setEmailing(false)}
        />
      ) : null}
    </div>
  );
}
